
import { Component, inject, resource, Signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators, } from '@angular/forms';

import {signIn, confirmSignIn, signUp, AuthError, fetchAuthSession } from 'aws-amplify/auth'
import { generatePassword, getUsername, UsersService } from '../../../services/users/users.service';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { UserStore } from '../../../stores/users.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom, map } from 'rxjs';
import { RequirementsService } from '../../../services/requirements/requirements.service';
import { AlertComponent } from "../../../components/alert/alert.component";
import { Requirements } from '../../../services/requirements/requirements.model';
import { CommonModule } from '@angular/common';

export function createEmailSuffixValidator(requirements:Signal<Requirements|undefined>): ValidatorFn {
    return (control:AbstractControl) : ValidationErrors | null => {

        const value = control.value;

        if (!value) {
            return null;
        }

        if (!requirements() || !requirements()!.emailSuffixes || requirements()!.emailSuffixes.length == 0) {
            return null;
        } 

        for (let suffix of requirements()!.emailSuffixes) {
            if (value.toLowerCase().endsWith(suffix.toLowerCase())) {
                return null;
            }
        }

        return {invalidEmailSuffix: true};
    }
}

@Component({
  selector: 'app-signin',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, AlertComponent],
  templateUrl: './signin.component.html',
  styleUrl: './signin.component.css'
})
export class SigninComponent {


  private $user = inject(UserStore) ;
  private $users = inject(UsersService) ;
  private $requirements = inject(RequirementsService) ;
  private $route = inject(ActivatedRoute) ;
  private $router = inject(Router) ;

  step: 'SIGNIN' | 'SIGNUP' | 'CONFIRM_SIGNIN' | 'REGISTER_GUEST' | 'COMPLETED' = 'SIGNIN' ;

  

  returnUrl = toSignal(
    this.$route.queryParams.pipe(map(params => params['returnUrl']))
  );

  requirements = resource({
    params: () => ({returnUrl: this.returnUrl()}),
    loader: ({params}) => firstValueFrom(this.$requirements.getRequirementsFromReturnUrl(params.returnUrl)),
  });

  signInForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email, createEmailSuffixValidator(this.requirements.value)]),
  });

  signUpForm = new FormGroup({
    givenName: new FormControl('', [Validators.required]),
    familyName: new FormControl('', [Validators.required])
  }) ;

  registerGuestForm = new FormGroup({
    givenName: new FormControl('', [Validators.required]),
    familyName: new FormControl('', [Validators.required])
  }) ;

  confirmForm = new FormGroup({
    code: new FormControl('', [Validators.required]) 
  });


  loading: boolean = false ;

  

  get email(): string | null {
    return this.signInForm.get('email')!.value ;
  }

 
  async signIn() {

    const email = this.signInForm.get('email')!.value! ;

    let userExists = await firstValueFrom(this.$users.checkAccountExists(email)) ;
    console.log("user exists", userExists) ;
    if (!userExists) {
      this.step = 'SIGNUP' ;
      return ;
    }

    let signinResult ;
    try {
      signinResult = await signIn({
        username: getUsername(email),
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP'
        },
      });
    } catch (err) {
      const authError = err as AuthError ;

      if (authError.name == 'UserNotFoundException') {
        this.step = 'SIGNUP';
      } else {
        console.log(authError.name, authError.cause, authError.recoverySuggestion) ;
        this.signInForm.setErrors({"signinFailed": true});
      }
      return ;
    }

    console.log("signin result", signinResult) ;
  
    if (signinResult.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      this.step = 'CONFIRM_SIGNIN' ;
    } else {
      console.log("unexpected next step", signinResult.nextStep.signInStep) ;
      this.signInForm.setErrors({"signinFailed": true});
    }
  }

  continueAsGuest() {
    this.step = 'REGISTER_GUEST' ;
  }

  async signUp() {

    const email = this.signInForm.get('email')!.value! ;
    const givenName = this.signUpForm.get('givenName')!.value! ;
    const familyName = this.signUpForm.get('familyName')!.value! ;
    const password = generatePassword();
    
    let signupResult ;
    try {
      signupResult = await signUp({
        username: getUsername(email),
        password: password,
        options: {
          userAttributes: {
            email: email,
            name: givenName + " " + familyName,
            given_name: givenName,
            family_name: familyName
          }
        }
      });
    } catch (err) {
      const authError = err as AuthError ;
      console.log(authError.name, authError.cause, authError.recoverySuggestion) ;
      this.signUpForm.setErrors({"signupFailed": true}) ;
      return ;
    }

    console.log("signup result", signupResult) ;

    if (signupResult.nextStep.signUpStep === 'DONE') {
      await this.signIn();
    } else {
      console.log("Unexpected next step", signupResult.nextStep.signUpStep) ;
      this.signUpForm.setErrors({"signupIncomplete": true}) ;
    }
  }


  async confirmSignIn() {
    
    const code = this.confirmForm.get('code')!.value!.trim() ;

    let confirmResult ;
    try {
      confirmResult = await confirmSignIn({
        challengeResponse: code
      });
    } catch (err) {
      const authError = err as AuthError ;

      if (authError.name == 'CodeMismatchException') {
        this.confirmForm.get('code')!.setErrors({"invalidCode": "Invalid Code"}) ;
      } else {
        this.confirmForm.get('code')!.setErrors({"signinIncomplete": true}) ;
      }

      this.confirmForm.get('code')!.markAsTouched();

      console.log(authError.name, authError.cause, authError.recoverySuggestion) ;
      return ;
    }

    if (confirmResult.nextStep.signInStep === 'DONE') {
      this.step = 'COMPLETED' ;
      await this.redirect();
    } else {
      console.log("Unexpected next step", confirmResult.nextStep.signInStep) ;
      this.signUpForm.setErrors({"signinIncomplete": true}) ;
    }
  }

  async createGuestAccount() { 

    const givenName = this.registerGuestForm.get('givenName')!.value! ;
    const familyName = this.registerGuestForm.get('familyName')!.value! ;

    await (this.$user.registerGuestUser(givenName, familyName)) ;
    console.log("guest registered", this.$user.user()) ;
    
    this.step = 'COMPLETED' ;
    await this.redirect() ;
  }

  async redirect() {

    console.log("redirecting...") ;

    await this.$user.load() ;

    console.log(this.$user.user(), this.returnUrl()) ;

    if (this.returnUrl()) {
      await this.$router.navigateByUrl(this.returnUrl());
    } else {
      await this.$router.navigateByUrl('/');
    }
  }
}
